import { useState, useRef, useEffect } from 'react';
import {
  FaSatelliteDish,
  FaEnvelope,
  FaPaperPlane,
  FaArrowLeft,
  FaCircleCheck,
  FaCircleXmark,
} from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { AuthService } from '../../../services/AuthService';

/* ============================================
   HOOK: Network Canvas
   ============================================ */
function useNetworkCanvas(canvasRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let mouse = { x: -9999, y: -9999 };

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const count = Math.min(55, Math.floor((canvas.width * canvas.height) / 22000));
    const nodes = Array.from({ length: count }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      r: Math.random() * 1.6 + 0.5,
      o: Math.random() * 0.3 + 0.08,
    }));

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);

    const maxD = 120;
    const mouseD = 170;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
        const dm = Math.hypot(mouse.x - n.x, mouse.y - n.y);
        let op = n.o;
        let rad = n.r;
        if (dm < mouseD) {
          op = Math.min(1, op + (1 - dm / mouseD) * 0.5);
          rad += (1 - dm / mouseD) * 2;
        }
        ctx.beginPath();
        ctx.arc(n.x, n.y, rad, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,180,216,${op})`;
        ctx.fill();
      }
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const d = Math.hypot(nodes[i].x - nodes[j].x, nodes[i].y - nodes[j].y);
          if (d < maxD) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(0,180,216,${(1 - d / maxD) * 0.08})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
        const dm = Math.hypot(mouse.x - nodes[i].x, mouse.y - nodes[i].y);
        if (dm < mouseD) {
          ctx.beginPath();
          ctx.moveTo(nodes[i].x, nodes[i].y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.strokeStyle = `rgba(72,202,228,${(1 - dm / mouseD) * 0.18})`;
          ctx.lineWidth = 0.6;
          ctx.stroke();
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
    };
  }, [canvasRef]);
}

/* ============================================
   KOMPONEN: ForgotPassword
   ============================================ */
export const ForgotPassword = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  useNetworkCanvas(canvasRef);

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  // 'idle' → 'sending' → 'success' / 'error'
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  const validate = () => {
    if (!email.trim()) {
      setError('Email wajib diisi');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Format email tidak valid');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    setStatus('sending');

    try {
      navigate('/email-verification');
      // await AuthService.forgotPassword({ data: { email } });
      // setStatus('success');
    } catch (err) {
      // setStatus('error');
      // const msg =
      //   err?.response?.data?.message ||
      //   err?.response?.data?.error ||
        // 'Gagal mengirim kode verifikasi. Silakan coba lagi.';
      // setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setIsLoading(true);
    try {
      await AuthService.forgotPassword({ data: { email } });
      setStatus('success');
    } catch {
      setError('Gagal mengirim ulang kode. Silakan coba lagi.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputCls = `w-full pl-12 pr-4 py-3.5 text-sm rounded-xl transition-all duration-300 outline-none font-body ${
    error && status !== 'success'
      ? 'bg-red-500/8 border border-red-500/40 text-white placeholder-red-300/40 focus:border-red-400 focus:ring-1 focus:ring-red-500/30'
      : 'bg-navy-700/50 border border-navy-500/40 text-white placeholder-slate-custom/40 focus:border-ocean-600/60 focus:ring-1 focus:ring-ocean-600/30'
  }`;

  return (
    <>
      <Helmet>
        <title>Lupa Password | Radius Trend - Billing Management System</title>
        <meta
          name="description"
          content="Lupa password akun Radius Trend Anda? Masukkan email untuk menerima kode verifikasi dan reset password."
        />
      </Helmet>

      <main className="relative min-h-screen flex items-center justify-center bg-navy-900 p-4 sm:p-6 overflow-hidden">
        {/* ===== Network Canvas ===== */}
        <canvas
          ref={canvasRef}
          className="absolute top-0 left-0 w-full h-full z-0"
          aria-hidden="true"
        />

        {/* ===== Gradient Orbs ===== */}
        <div
          className="absolute w-[450px] h-[450px] bg-ocean-900/25 top-[-15%] right-[-8%] rounded-full"
          style={{ filter: 'blur(100px)', animation: 'float 9s ease-in-out infinite' }}
          aria-hidden="true"
        />
        <div
          className="absolute w-[350px] h-[350px] bg-ocean-800/15 bottom-[-10%] left-[-5%] rounded-full"
          style={{ filter: 'blur(90px)', animation: 'floatSlow 11s ease-in-out infinite' }}
          aria-hidden="true"
        />
        <div
          className="absolute w-[200px] h-[200px] bg-navy-500/15 top-[50%] left-[15%] rounded-full"
          style={{ filter: 'blur(70px)', animation: 'float 13s ease-in-out 2s infinite' }}
          aria-hidden="true"
        />

        {/* ===== Card ===== */}
        <article
          className={`
            relative z-10 w-full max-w-md
            rounded-3xl overflow-hidden
            transition-all duration-1000 cubic-bezier(0.16, 1, 0.3, 1)
            ${mounted ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-[0.97]'}
          `}
          style={{
            background: 'rgba(11, 20, 38, 0.65)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            border: '1px solid rgba(0, 180, 216, 0.1)',
            boxShadow:
              '0 25px 60px rgba(0, 0, 0, 0.5), 0 0 80px rgba(0, 180, 216, 0.06)',
          }}
        >
          {/* ===== Top decorative line ===== */}
          <div
            className="h-px w-full"
            style={{
              background:
                'linear-gradient(90deg, transparent, rgba(0, 180, 216, 0.3), transparent)',
            }}
            aria-hidden="true"
          />

          <div className="p-8 sm:p-10">
            {/* ===== Header ===== */}
            <header className="text-center mb-8">
              {/* Logo */}
              <div className="flex justify-center mb-6">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(0, 180, 216, 0.2), rgba(0, 119, 182, 0.1))',
                    border: '1px solid rgba(0, 180, 216, 0.2)',
                    boxShadow: '0 8px 24px rgba(0, 180, 216, 0.15)',
                  }}
                >
                  <FaSatelliteDish
                    className="text-2xl text-ocean-500"
                    aria-hidden="true"
                  />
                </div>
              </div>

              {/* H1 untuk SEO */}
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-white mb-2 tracking-tight">
                {status === 'success' ? 'Cek Email Anda' : 'Lupa Password?'}
              </h1>
              <p className="text-sm text-slate-custom leading-relaxed">
                {status === 'success'
                  ? 'Kami telah mengirim kode verifikasi ke:'
                  : 'Masukkan email yang terdaftar. Kami akan mengirimkan kode verifikasi untuk mereset password Anda.'}
              </p>
            </header>

            {/* ===== Success State ===== */}
            {status === 'success' && (
              <div
                className="mb-8 p-4 rounded-xl text-center"
                style={{
                  background: 'rgba(0, 180, 216, 0.06)',
                  border: '1px solid rgba(0, 180, 216, 0.15)',
                  animation: 'fadeIn 0.5s ease-out',
                }}
              >
                <div
                  className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
                  style={{
                    background: 'rgba(0, 180, 216, 0.12)',
                    border: '1px solid rgba(0, 180, 216, 0.25)',
                  }}
                >
                  <FaCircleCheck className="text-2xl text-ocean-500" />
                </div>
                <p className="text-ocean-400 text-sm font-semibold break-all">
                  {email}
                </p>
                <p className="text-slate-custom text-xs mt-2">
                  Kode verifikasi berlaku selama 5 menit. Cek folder spam jika
                  tidak ditemukan di inbox.
                </p>
              </div>
            )}

            {/* ===== Error State (non-success) ===== */}
            {status === 'error' && error && (
              <div
                className="mb-6 p-4 rounded-xl flex items-start gap-3"
                style={{
                  background: 'rgba(239, 68, 68, 0.06)',
                  border: '1px solid rgba(239, 68, 68, 0.15)',
                  animation: 'fadeIn 0.5s ease-out',
                }}
                role="alert"
              >
                <FaCircleXmark className="text-red-400 text-lg flex-shrink-0 mt-0.5" />
                <p className="text-red-400 text-sm leading-relaxed">{error}</p>
              </div>
            )}

            {/* ===== Form ===== */}
            {status !== 'success' ? (
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold text-slate-custom mb-2"
                  >
                    Alamat Email
                  </label>
                  <div className="relative">
                    <FaEnvelope
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-custom/50 text-base z-10"
                      aria-hidden="true"
                    />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError('');
                        if (status === 'error') setStatus('idle');
                      }}
                      placeholder="contoh@email.com"
                      className={inputCls}
                      autoFocus
                    />
                  </div>
                  {error && status !== 'error' && (
                    <p className="text-xs text-red-400 font-medium mt-1.5 flex items-center gap-1.5">
                      <span
                        className="inline-block w-1 h-1 rounded-full bg-red-400"
                        aria-hidden="true"
                      />
                      {error}
                    </p>
                  )}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full justify-center text-base py-4"
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>Mengirim Kode...</span>
                    </>
                  ) : (
                    <>
                      <span>Kirim Kode Verifikasi</span>
                      <FaPaperPlane className="text-base" />
                    </>
                  )}
                </button>
              </form>
            ) : (
              /* ===== Success Actions ===== */
              <div
                className="space-y-4"
                style={{ animation: 'slideUp 0.5s ease-out 0.2s both' }}
              >
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isLoading}
                  className="btn-secondary w-full justify-center text-sm py-3.5"
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4 text-ocean-500"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>Mengirim Ulang...</span>
                    </>
                  ) : (
                    <>
                      <FaPaperPlane className="text-sm" />
                      <span>Kirim Ulang Kode</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="w-full py-3.5 px-4 rounded-xl text-sm font-semibold text-slate-custom border border-navy-500/40 hover:text-white hover:border-navy-400/50 hover:bg-navy-700/30 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <FaArrowLeft className="text-xs" />
                  <span>Kembali ke Login</span>
                </button>
              </div>
            )}

            {/* ===== Back to Login (form state) ===== */}
            {status !== 'success' && (
              <footer className="mt-8 text-center text-sm text-slate-custom">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="inline-flex items-center gap-2 text-ocean-500 font-semibold hover:text-ocean-400 transition-colors"
                >
                  <FaArrowLeft className="text-xs" />
                  <span>Kembali ke Halaman Login</span>
                </button>
              </footer>
            )}

            {/* ===== Info Box ===== */}
            <div
              className="mt-8 pt-6 border-t border-navy-500/20"
              style={{ animation: 'fadeIn 0.8s ease-out 0.5s both' }}
            >
              <div className="flex gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{
                    background: 'rgba(0, 180, 216, 0.08)',
                    border: '1px solid rgba(0, 180, 216, 0.12)',
                  }}
                >
                  <FaEnvelope className="text-ocean-700 text-xs" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-xs text-slate-custom leading-relaxed">
                    Pastikan email yang Anda masukkan adalah email yang terdaftar
                    di akun Radius Trend. Kode verifikasi akan dikirimkan dalam
                    waktu kurang dari 1 menit.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ===== Bottom decorative line ===== */}
          <div
            className="h-px w-full"
            style={{
              background:
                'linear-gradient(90deg, transparent, rgba(0, 180, 216, 0.2), transparent)',
            }}
            aria-hidden="true"
          />
        </article>

        {/* ===== Bottom Copyright ===== */}
        <p
          className="absolute bottom-4 left-0 right-0 text-center text-xs text-slate-custom/30 z-10"
          style={{ animation: 'fadeIn 1s ease-out 1s both' }}
        >
          &copy; {new Date().getFullYear()} Radius Trend. Seluruh hak cipta
          dilindungi.
        </p>
      </main>

      {/* ===== Inline Styles Fallback ===== */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        @keyframes floatSlow {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(3deg); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .font-display { font-family: 'Space Grotesk', sans-serif; }
        .font-body { font-family: 'Plus Jakarta Sans', sans-serif; }
        body { font-family: 'Plus Jakarta Sans', sans-serif; }

        .bg-navy-900 { background-color: #060D1F; }
        .bg-navy-700\/50 { background-color: rgba(15, 26, 46, 0.5); }
        .border-navy-500\/40 { border-color: rgba(26, 45, 74, 0.4); }
        .border-navy-500\/20 { border-color: rgba(26, 45, 74, 0.2); }
        .border-navy-400\/50 { border-color: rgba(36, 59, 92, 0.5); }
        .text-slate-custom { color: #8899AA; }
        .text-slate-custom\/40 { color: rgba(136, 153, 170, 0.4); }
        .text-slate-custom\/50 { color: rgba(136, 153, 170, 0.5); }
        .text-slate-custom\/30 { color: rgba(136, 153, 170, 0.3); }
        .text-ocean-500 { color: #48CAE4; }
        .text-ocean-400 { color: #90E0EF; }
        .text-ocean-600 { color: #00B4D8; }
        .text-ocean-700 { color: #0096C7; }
        .text-red-400 { color: #f87171; }
        .text-red-300\/40 { color: rgba(252, 165, 165, 0.4); }
        .border-ocean-600\/60 { border-color: rgba(0, 180, 216, 0.6); }
        .border-ocean-600\/30 { border-color: rgba(0, 180, 216, 0.3); }
        .border-red-500\/40 { border-color: rgba(239, 68, 68, 0.4); }
        .border-red-400 { border-color: rgba(248, 113, 113, 1); }
        .ring-ocean-600\/30 { --tw-ring-color: rgba(0, 180, 216, 0.3); }
        .ring-red-500\/30 { --tw-ring-color: rgba(239, 68, 68, 0.3); }
        .bg-red-500\/8 { background-color: rgba(239, 68, 68, 0.08); }
        .bg-navy-700\/30 { background-color: rgba(15, 26, 46, 0.3); }
        .focus\:border-ocean-600\/60:focus { border-color: rgba(0, 180, 216, 0.6); }
        .focus\:border-red-400:focus { border-color: rgba(248, 113, 113, 1); }
        .focus\:ring-ocean-600\/30:focus { --tw-ring-color: rgba(0, 180, 216, 0.3); }
        .focus\:ring-red-500\/30:focus { --tw-ring-color: rgba(239, 68, 68, 0.3); }
        .hover\:text-white:hover { color: #F0F4F8; }
        .hover\:border-navy-400\/50:hover { border-color: rgba(36, 59, 92, 0.5); }
        .hover\:bg-navy-700\/30:hover { background-color: rgba(15, 26, 46, 0.3); }

        .btn-primary {
          background: linear-gradient(135deg, #0077B6, #00B4D8);
          color: #fff;
          padding: 14px 32px;
          border-radius: 12px;
          font-weight: 600;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
          border: none;
          cursor: pointer;
          font-size: inherit;
          font-family: inherit;
        }
        .btn-primary::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent);
          transition: left 0.6s ease;
        }
        .btn-primary:hover::before { left: 100%; }
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 35px rgba(0, 180, 216, 0.4);
        }
        .btn-primary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none !important;
          box-shadow: none !important;
        }

        .btn-secondary {
          background: transparent;
          color: #48CAE4;
          padding: 14px 32px;
          border-radius: 12px;
          font-weight: 600;
          border: 2px solid rgba(0, 180, 216, 0.35);
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
          cursor: pointer;
          font-size: inherit;
          font-family: inherit;
        }
        .btn-secondary:hover {
          background: rgba(0, 180, 216, 0.08);
          border-color: #00B4D8;
          transform: translateY(-2px);
        }
        .btn-secondary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none !important;
        }
      `}</style>
    </>
  );
};